import React from 'react';

import Input from '../components/Input';
import Dropdown from '../components/Dropdown';
import FormWaterTariff from '../features/Devices/waterTariffs/FormWaterTariff';
import { initialConfiguration, initialDevs } from '../features/Devices/constants/constants';
import LabelWithTooltip from '../components/LabelWithTooltip';
import AlertAccent from '../components/AlertAccent';
import { useDevicesStore } from '../stores';
import ChannelSettings from '../deviceList/ChannelSettings';
import { generateControllerSerialNumber } from '../utils/generateSerialNumber';
import ControllerFormFields from '../deviceList/ControllerFormFields';

const DeviceAdder = ({ setDevice, device, objects, objectId, setObjectId, setThreshold, threshold, deviceInputs, setDeviceInputs }) => {
  const [index, setIndex] = React.useState({
    type: 0,
    subtype: 0,
    output: 0,
  });

  const { devices } = useDevicesStore();
  const hasCountersOrSensors = devices?.some((item) => item?.type?.key === 'counter' || item?.type?.key === 'sensor');
  const isController = device.device_type === 'controller';

  // deviceToEdit для ChannelSettings:
  const deviceToEdit = React.useMemo(() => {
    if (device.device_type === 'controller') {
      const serial_number = device.serial_letters && device.serial_numbers ? generateControllerSerialNumber(device.serial_letters, device.serial_numbers) : '';
      return {
        ...device,
        serial_number,
      };
    }
    return null;
  }, [device]);

  const onTypeChange = (i) => {
    const newDeviceType = initialDevs[i].device_type;
    const { config, ...deviceWithoutConfig } = device;

    const updatedDevice = {
      ...deviceWithoutConfig,
      device_type: newDeviceType,
      device_sub_type: initialDevs[i].sub[0].device_type,
      output_type: initialDevs[i].sub[0].output_type[0].key,
    };

    // config только для контроллера
    if (newDeviceType === 'controller') {
      updatedDevice.config = {
        write_every: 4,
        conn_rule: ['D', 0, 21],
      };
    }

    setIndex({ ...index, type: i, subtype: 0, output: 0 });
    setDevice(updatedDevice);
  };

  const onSubTypeChange = (i) => {
    setIndex({ ...index, subtype: i, output: 0 });
    setDevice({
      ...device,
      device_sub_type: initialDevs[index.type].sub[i].device_type,
      output_type: initialDevs[index.type].sub[i].output_type[0].key,
    });
  };

  return (
    <>
      <Dropdown
        className={'w-full'}
        label={'Тип устройств'}
        options={initialDevs}
        value={index.type}
        inputCb={(e) => onTypeChange(parseInt(e.target.value))}
        id="type"
        valkey="device_type_i"
        optkey="category"
      />
      {isController && !hasCountersOrSensors ? (
        <AlertAccent
          classContainer={'mt-3'}
          title="Счетчики и датчики не добавлены!"
          text="Для добавления контроллера Вам необходимо добавить хотя бы одно устройство"
          showAlert={true}
        />
      ) : (
        <>
          <Input
            classContainer="w-full mt-4"
            value={device.description}
            inputCb={(e) =>
              setDevice({
                ...device,
                description: e.target.value,
              })
            }
            label="Место установки устройства"
            id="descr"
            type="text"
            placeholder={'Например: ванная, санузел, кухня'}
          />

          <Dropdown
            className={'w-full mt-4'}
            label={'Подтип устройств'}
            options={initialDevs[index.type].sub}
            value={index.subtype}
            inputCb={(e) => onSubTypeChange(parseInt(e.target.value))}
            id="type"
            valkey="device_subtype_i"
            optkey="category"
          />

          {device.device_type === 'counter' && (
            <Input
              classContainer={'w-full mt-4'}
              value={device.serial_number}
              inputCb={(e) =>
                setDevice({
                  ...device,
                  serial_number: e.target.value,
                })
              }
              label="Серийный номер"
              id="serial_number"
              type="text"
              placeholder={'A123'}
            />
          )}
          {isController && (
            <>
              <Input
                classContainer={'mt-4'}
                value={device.serial_letters}
                inputCb={(e) => {
                  const value = e.target.value;
                  const serial_letters = value.replace(/\d/g, '').toUpperCase();
                  setDevice({ ...device, serial_letters });
                }}
                autoCapitalize="words"
                label="Буквенная часть серийного номера"
                id="serial_letters"
                keyboardType={'default'}
                placeholder={'Введите буквы'}
                type="text"
                maxLength={4}
              />
              <Input
                classContainer={'mt-4'}
                value={device.serial_numbers}
                inputCb={(e) => {
                  setDevice({ ...device, serial_numbers: e.target.value });
                }}
                label={
                  <LabelWithTooltip
                    tooltipImage={'/images/serialNumber.png'}
                    text={'Цифровая часть серийного номера'}
                    tooltipText={'Цифровая часть серийного номера без нулей'}
                  />
                }
                id="serial_numbers"
                type="number"
                placeholder={'Введите цифры (допускается ввод без нулей)'}
              />
              <ControllerFormFields deviceToEdit={device} setDeviceToEdit={setDevice} />
            </>
          )}
          <Input
            classContainer={'w-full mt-4'}
            value={device.manufacturer}
            inputCb={(e) =>
              setDevice({
                ...device,
                manufacturer: e.target.value,
              })
            }
            label="Производитель"
            id="manufacturer"
            type="text"
            placeholder={'Меридиан'}
          />
          {device.device_type === 'counter' && (
            <>
              <Input
                classContainer={'w-full mt-4'}
                value={threshold}
                inputCb={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setThreshold('');
                  } else {
                    const num = Number(val);
                    if (num >= 0) {
                      setThreshold(num);
                    }
                  }
                }}
                label="Дневной порог расхода воды, литры"
                id="threshold"
                type="number"
                placeholder={'1'}
              />
              <FormWaterTariff />
              <div className={'text-xs mt-1 ml-2 text-gray-600'}>*На основе указанных тарифов мы покажем вашу ориентировочную сумму затрат</div>
            </>
          )}
          <Dropdown
            className="w-full mt-4"
            label={'Привязать к объекту'}
            options={objects}
            inputCb={(e) =>
              setObjectId({
                ...objectId,
                object_id: parseInt(e.target.value),
              })
            }
            id="devs"
            valkey="id"
            optkey="address"
          />
          {isController && (
            <ChannelSettings
              setDeviceInputs={setDeviceInputs}
              initialConfiguration={initialConfiguration}
              deviceInputs={deviceInputs || []}
              devices={devices}
              deviceToEdit={deviceToEdit}
            />
          )}
        </>
      )}
    </>
  );
};
export default DeviceAdder;
